import { useCallback } from 'react';
import { useInterval } from 'react-use';
import TimedProgressBar from 'src/components/TimedProgresBar';
import AlertNotificationAction from 'src/components/Toasts/AlertNotifications/AlertNotificationAction';
import useNotificationStore from 'src/stores/useNotificationStore';
import { NotificationData } from 'src/utils/notifications';
import AlertToast from '../AlertToast';
import { useSwipeToDismiss } from 'src/components/Toasts/hooks/useSwipeToDismiss';
import { animated } from '@react-spring/web';

const NOTIFICATION_DURATION_MILLIS = 10000;

interface Props {
	notifications: NotificationData[];
	onDismiss: (id: string) => void;
}

export default function MobileAlertNotifications({
	notifications,
	onDismiss,
}: Props) {
	const notificationStore = useNotificationStore();

	// On mobile we can just take the top notification and remove it on an interval.
	const notification = notifications[0];
	const { message, action, id, disableAlertAutoDismiss } = notification;

	const dismissCurrentNotification = useCallback(() => {
		if (id) {
			notificationStore.removeNotificationById(id);
			onDismiss(id);
		}
	}, [id, notificationStore, onDismiss]);

	const dismissAllNotifications = useCallback(() => {
		notificationStore.clearAllNotifications((n) => n.type === 'alert');
		notifications.forEach((n) => {
			if (n.id) {
				onDismiss(n.id);
			}
		});
	}, [notificationStore, notifications, onDismiss]);

	const swipeToDismissProps = useSwipeToDismiss({
		onDismiss: dismissAllNotifications,
	});

	useInterval(
		dismissCurrentNotification,
		disableAlertAutoDismiss ? null : NOTIFICATION_DURATION_MILLIS
	);

	const content = (() => {
		// Without somewhere to go, the action renders as a bare arrow.
		const hasTarget =
			!!action && (('href' in action && !!action.href) || 'callback' in action);

		if (hasTarget) {
			return (
				<AlertNotificationAction
					alertType="minor"
					action={action}
					className="text-left whitespace-normal text-text-default"
					dismissNotification={dismissCurrentNotification}
				>
					<span className="typo-label-sm">{message}</span>
				</AlertNotificationAction>
			);
		}

		return <span className="typo-label-sm">{message}</span>;
	})();

	return (
		<animated.div {...swipeToDismissProps}>
			<AlertToast>
				<div className="p-lg">{content}</div>
				{!disableAlertAutoDismiss && (
					<TimedProgressBar
						durationMillis={NOTIFICATION_DURATION_MILLIS}
						key={id}
					/>
				)}
			</AlertToast>
		</animated.div>
	);
}
